import axios from 'axios';
import type {
  AdminUser,
  AdminUserCreatePayload,
  AdminUserUpdatePayload,
  AuditLogEntry,
  AuditLogFilters,
  AutomationPermission,
  AutomationPermissionUpdatePayload,
  PaginatedResponse,
} from '@/lib/types';

export const apiClient = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    const retryConfig = config as typeof config & { __retriedAfterRefresh?: boolean };
    const url = String(config?.url ?? '');
    if (
      status === 401 &&
      retryConfig &&
      !retryConfig.__retriedAfterRefresh &&
      !url.includes('/auth/refresh') &&
      !url.includes('/auth/google') &&
      !url.includes('/auth/logout')
    ) {
      retryConfig.__retriedAfterRefresh = true;
      await apiClient.post('/auth/refresh');
      return apiClient(retryConfig);
    }
    return Promise.reject(error);
  },
);

function normalizePaginated<T>(body: T[] | PaginatedResponse<T>): PaginatedResponse<T> {
  if (Array.isArray(body)) {
    return {
      items: body,
      total: body.length,
      page: 1,
      page_size: body.length,
    };
  }
  return {
    ...body,
    page: body.page ?? 1,
    page_size: body.page_size ?? body.items.length,
  };
}

function pathPart(value: string): string {
  return encodeURIComponent(value);
}

export const adminApi = {
  async listUsers(): Promise<PaginatedResponse<AdminUser>> {
    const res = await apiClient.get<AdminUser[] | PaginatedResponse<AdminUser>>(
      '/admin/users',
    );
    return normalizePaginated(res.data);
  },

  async createUser(payload: AdminUserCreatePayload): Promise<AdminUser> {
    const res = await apiClient.post<AdminUser>('/admin/users', payload);
    return res.data;
  },

  async updateUser(email: string, payload: AdminUserUpdatePayload): Promise<AdminUser> {
    const res = await apiClient.patch<AdminUser>(
      `/admin/users/${pathPart(email)}`,
      payload,
    );
    return res.data;
  },

  async listUserPermissions(email: string): Promise<AutomationPermission[]> {
    const res = await apiClient.get<AutomationPermission[] | PaginatedResponse<AutomationPermission>>(
      `/admin/users/${pathPart(email)}/permissions`,
    );
    return normalizePaginated(res.data).items;
  },

  async updateUserPermission(
    email: string,
    slug: string,
    payload: AutomationPermissionUpdatePayload,
  ): Promise<AutomationPermission> {
    const body = {
      can_view: payload.can_view,
      can_comment: payload.can_comment,
      can_reprocess: payload.can_reprocess,
      can_approve_post: payload.can_approve_post,
      can_reject: payload.can_reject,
      can_audit_view: payload.can_audit_view,
    };
    const res = await apiClient.put<AutomationPermission>(
      `/admin/users/${pathPart(email)}/permissions/${pathPart(slug)}`,
      body,
    );
    return res.data;
  },

  async listAudit(filters: AuditLogFilters): Promise<PaginatedResponse<AuditLogEntry>> {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value != null),
    );
    const res = await apiClient.get<AuditLogEntry[] | PaginatedResponse<AuditLogEntry>>(
      '/admin/audit',
      { params },
    );
    return normalizePaginated(res.data);
  },
};
